import { axios } from '@pukka/auth';
import { MOKA_GOAUTH_SERVICE } from '@pukka/common/constants';
import { AxiosError } from 'axios';

import { BrandsModel } from '../../models';

const MOKA_INVENTORY_SERVICE = `${MOKA_GOAUTH_SERVICE}/inventory/v1`;

interface MutationError {
  readonly code?: string;
  readonly errors?: string;
  readonly message?: string;
}

/* istanbul ignore next */
const reject = (error: AxiosError<MutationError>) => {
  if (!error.response) {
    throw error;
  }

  const {
    data: { code, message },
  } = error.response;

  if (code) {
    error.code = code;
  }

  if (message) {
    error.message = message;
  }

  throw error;
};

async function getParentList(outletId: number, params: BrandsModel.GetParentListParams): Promise<BrandsModel.GetParentListResponse> {
  return axios
    .get(`${MOKA_INVENTORY_SERVICE}/principals`, {
      params,
      headers: {
        outletId,
      },
    })
    .then((resp) => resp.data)
    .catch(reject);
}

async function getBrandList(outletId: number, params: BrandsModel.GetBrandListParams): Promise<BrandsModel.GetBrandListResponse> {
  return axios
    .get(`${MOKA_INVENTORY_SERVICE}/brands/outlets/${outletId}`, { params })
    .then((resp) => resp.data)
    .catch(reject);
}

async function getParentDetail(principalId: number, outletId: number): Promise<BrandsModel.ParentDetail> {
  return axios
    .get(`${MOKA_INVENTORY_SERVICE}/principals/${principalId}`, {
      headers: {
        outletId,
      },
    })
    .then((resp) => resp.data)
    .catch(reject);
}

async function getBrandDetail(brandId: number, outletId: number): Promise<BrandsModel.BrandDetail> {
  return axios
    .get(`${MOKA_INVENTORY_SERVICE}/brands/${brandId}/outlets/${outletId}`)
    .then((resp) => resp.data)
    .catch(reject);
}

async function createBrand(outletId: number, payload: BrandsModel.CreateBrandRequest) {
  return axios
    .post(`${MOKA_INVENTORY_SERVICE}/brands/outlets/${outletId}`, payload)
    .then((resp) => resp.data)
    .catch(reject);
}

async function updateBrand(outletId: number, brandId: number, payload: BrandsModel.UpdateBrandRequest) {
  return axios
    .put(`${MOKA_INVENTORY_SERVICE}/brands/${brandId}/outlets/${outletId}`, payload)
    .then((resp) => resp.data)
    .catch(reject);
}

async function deleteBrand(brandId: number, outletId: number, isAllOutlet: boolean) {
  return axios
    .delete(`${MOKA_INVENTORY_SERVICE}/brands/${brandId}/outlets/${outletId}`, {
      params: {
        is_saved_to_all_outlets: isAllOutlet,
      },
    })
    .then((resp) => resp.data)
    .catch(reject);
}

async function createBrandAllOutlet(outletId: number, payload: BrandsModel.UpsertBrandToAllOutletsRequest) {
  return axios
    .post(`${MOKA_INVENTORY_SERVICE}/brands/outlets/${outletId}/all`, payload)
    .then((resp) => resp.data)
    .catch(reject);
}

async function createParent(payload: BrandsModel.CreateParentRequest) {
  return axios
    .post(`${MOKA_INVENTORY_SERVICE}/principals`, payload, {
      headers: {
        outletId: payload.outlet_id,
      },
    })
    .then((resp) => resp.data)
    .catch(reject);
}

async function updateParent(principalId: number, payload: BrandsModel.UpdateParentRequest) {
  return axios
    .put(`${MOKA_INVENTORY_SERVICE}/principals/${principalId}`, payload, {
      headers: {
        outletId: payload.outlet_id,
      },
    })
    .then((resp) => resp.data)
    .catch(reject);
}

async function deleteParent(principalId: number, outletId: number, isAllOutlet: boolean) {
  return axios
    .delete(`${MOKA_INVENTORY_SERVICE}/principals/${principalId}`, {
      headers: {
        outletId,
      },
      params: {
        is_saved_to_all_outlets: isAllOutlet,
      },
    })
    .then((resp) => resp.data)
    .catch(reject);
}

async function bulkDelete(outletId: number, payload: BrandsModel.DeleteBulkRequest) {
  return axios
    .delete(`${MOKA_INVENTORY_SERVICE}/principals/bulk`, {
      headers: {
        outletId,
      },
      data: payload,
    })
    .then((resp) => resp.data)
    .catch(reject);
}

async function bulkMove(outletId: number, payload: BrandsModel.MoveBulkRequest) {
  return axios
    .put(`${MOKA_INVENTORY_SERVICE}/principals/bulk`, payload, {
      headers: {
        outletId,
      },
    })
    .then((resp) => resp.data)
    .catch(reject);
}

export {
  getParentList,
  getBrandList,
  getParentDetail,
  getBrandDetail,
  createBrand,
  updateBrand,
  deleteBrand,
  createBrandAllOutlet,
  createParent,
  updateParent,
  deleteParent,
  bulkDelete,
  bulkMove,
};
